import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  where,
  writeBatch,
  type CollectionReference,
} from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import { stripUndefined } from '../utils/stripUndefined';

const BATCH_CHUNK_SIZE = 400;

type FirestoreDoc = Record<string, unknown> & { id?: string };

export interface CloneAuthorOptions {
  projectId: string;
  sourceAuthorId: string;
  displayName: string;
  includePosts?: boolean;
}

export interface CloneAuthorResult {
  newAuthorId: string;
  counts: {
    platforms: number;
    voiceDocs: number;
    designDocs: number;
    designProfileSets: number;
    designProfiles: number;
    designReferences: number;
    formats: number;
    postExamples: number;
    postTemplates: number;
    authorPosts: number;
    topicPillars: number;
  };
}

export class AuthorCloneService {
  private get db() {
    return CoreFirebaseService.getInstance().firestore;
  }

  async cloneAuthor(opts: CloneAuthorOptions): Promise<CloneAuthorResult> {
    const displayName = opts.displayName.trim();
    if (!displayName) throw new Error('Display name is required');

    const sourceRef = doc(this.db, 'projects', opts.projectId, 'authors', opts.sourceAuthorId);
    const sourceSnap = await getDoc(sourceRef);
    if (!sourceSnap.exists()) throw new Error(`Author ${opts.sourceAuthorId} not found`);

    const newAuthorId = `author_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
    const targetRef = doc(this.db, 'projects', opts.projectId, 'authors', newAuthorId);
    const sourceAuthor = sourceSnap.data() as FirestoreDoc;
    const authorPayload = this.rewriteDoc(sourceAuthor, opts.projectId, newAuthorId, {
      id: newAuthorId,
      displayName,
      isDefault: false,
      clonedFromAuthorId: opts.sourceAuthorId,
      clonedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
    });

    const counts: CloneAuthorResult['counts'] = {
      platforms: 0,
      voiceDocs: 0,
      designDocs: 0,
      designProfileSets: 0,
      designProfiles: 0,
      designReferences: 0,
      formats: 0,
      postExamples: 0,
      postTemplates: 0,
      authorPosts: 0,
      topicPillars: 0,
    };

    {
      const batch = writeBatch(this.db);
      batch.set(targetRef, authorPayload);
      await batch.commit();
    }

    const platformsSnap = await getDocs(collection(this.db, 'projects', opts.projectId, 'authors', opts.sourceAuthorId, 'platforms'));
    for (const platformDoc of platformsSnap.docs) {
      const platformId = platformDoc.id;
      const targetPlatformRef = doc(this.db, 'projects', opts.projectId, 'authors', newAuthorId, 'platforms', platformId);
      const batch = writeBatch(this.db);
      batch.set(targetPlatformRef, this.rewriteDoc(platformDoc.data() as FirestoreDoc, opts.projectId, newAuthorId, { id: platformId }));
      await batch.commit();
      counts.platforms += 1;

      const basePath = ['projects', opts.projectId, 'authors'] as const;
      counts.voiceDocs += await this.cloneFlatCollection(
        collection(this.db, ...basePath, opts.sourceAuthorId, 'platforms', platformId, 'voice'),
        collection(this.db, ...basePath, newAuthorId, 'platforms', platformId, 'voice'),
        opts.projectId,
        newAuthorId,
      );
      counts.designDocs += await this.cloneFlatCollection(
        collection(this.db, ...basePath, opts.sourceAuthorId, 'platforms', platformId, 'design'),
        collection(this.db, ...basePath, newAuthorId, 'platforms', platformId, 'design'),
        opts.projectId,
        newAuthorId,
      );
      counts.formats += await this.cloneFlatCollection(
        collection(this.db, ...basePath, opts.sourceAuthorId, 'platforms', platformId, 'formats'),
        collection(this.db, ...basePath, newAuthorId, 'platforms', platformId, 'formats'),
        opts.projectId,
        newAuthorId,
      );
      counts.postExamples += await this.cloneFlatCollection(
        collection(this.db, ...basePath, opts.sourceAuthorId, 'platforms', platformId, 'post_examples'),
        collection(this.db, ...basePath, newAuthorId, 'platforms', platformId, 'post_examples'),
        opts.projectId,
        newAuthorId,
      );
      counts.postTemplates += await this.cloneFlatCollection(
        collection(this.db, ...basePath, opts.sourceAuthorId, 'platforms', platformId, 'post_templates'),
        collection(this.db, ...basePath, newAuthorId, 'platforms', platformId, 'post_templates'),
        opts.projectId,
        newAuthorId,
      );
      if (opts.includePosts) {
        counts.authorPosts += await this.cloneFlatCollection(
          collection(this.db, ...basePath, opts.sourceAuthorId, 'platforms', platformId, 'author_posts'),
          collection(this.db, ...basePath, newAuthorId, 'platforms', platformId, 'author_posts'),
          opts.projectId,
          newAuthorId,
        );
      }

      const profileCounts = await this.cloneDesignProfileSets(opts.projectId, opts.sourceAuthorId, newAuthorId, platformId);
      counts.designProfileSets += profileCounts.sets;
      counts.designProfiles += profileCounts.profiles;
      counts.designReferences += profileCounts.references;
    }

    counts.topicPillars = await this.cloneTopicPillars(opts.projectId, opts.sourceAuthorId, newAuthorId, Boolean(opts.includePosts));

    return { newAuthorId, counts };
  }

  private async cloneDesignProfileSets(
    projectId: string,
    sourceAuthorId: string,
    newAuthorId: string,
    platformId: string,
  ): Promise<{ sets: number; profiles: number; references: number }> {
    const sourceSets = collection(this.db, 'projects', projectId, 'authors', sourceAuthorId, 'platforms', platformId, 'design_profile_sets');
    const targetSets = collection(this.db, 'projects', projectId, 'authors', newAuthorId, 'platforms', platformId, 'design_profile_sets');
    const setsSnap = await getDocs(sourceSets);
    let profiles = 0;
    let references = 0;

    for (const setDoc of setsSnap.docs) {
      const setTarget = doc(targetSets, setDoc.id);
      const batch = writeBatch(this.db);
      batch.set(setTarget, this.rewriteDoc(setDoc.data() as FirestoreDoc, projectId, newAuthorId, { id: setDoc.id }));
      await batch.commit();

      const sourceProfiles = collection(setDoc.ref, 'profiles');
      const targetProfiles = collection(setTarget, 'profiles');
      const profileSnap = await getDocs(sourceProfiles);
      profiles += profileSnap.docs.length;
      await this.writeDocs(
        targetProfiles,
        profileSnap.docs.map((profileDoc) => ({
          id: profileDoc.id,
          data: this.rewriteDoc(profileDoc.data() as FirestoreDoc, projectId, newAuthorId, { id: profileDoc.id }),
        })),
      );

      for (const profileDoc of profileSnap.docs) {
        references += await this.cloneFlatCollection(
          collection(profileDoc.ref, 'references'),
          collection(setTarget, 'profiles', profileDoc.id, 'references'),
          projectId,
          newAuthorId,
        );
      }
    }
    return { sets: setsSnap.docs.length, profiles, references };
  }

  private async cloneTopicPillars(
    projectId: string,
    sourceAuthorId: string,
    newAuthorId: string,
    includePosts: boolean,
  ): Promise<number> {
    const pillarsSnap = await getDocs(query(
      collection(this.db, 'projects', projectId, 'topic_pillars'),
      where('authorIds', 'array-contains', sourceAuthorId),
    ));
    const docs = pillarsSnap.docs.map((pillarDoc) => {
      const newPillarId = `pillar_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
      const data = this.rewriteDoc(pillarDoc.data() as FirestoreDoc, projectId, newAuthorId, {
        id: newPillarId,
        authorIds: [newAuthorId],
        clonedFromPillarId: pillarDoc.id,
        ...(includePosts ? {} : { sourcePostIds: [], sourceHighlights: [] }),
      });
      return { id: newPillarId, data };
    });
    await this.writeDocs(collection(this.db, 'projects', projectId, 'topic_pillars'), docs);
    return docs.length;
  }

  private async cloneFlatCollection(
    source: CollectionReference,
    target: CollectionReference,
    projectId: string,
    authorId: string,
  ): Promise<number> {
    const snap = await getDocs(source);
    await this.writeDocs(
      target,
      snap.docs.map((sourceDoc) => ({
        id: sourceDoc.id,
        data: this.rewriteDoc(sourceDoc.data() as FirestoreDoc, projectId, authorId, { id: sourceDoc.id }),
      })),
    );
    return snap.docs.length;
  }

  private async writeDocs(
    target: CollectionReference,
    docs: Array<{ id: string; data: Record<string, unknown> }>,
  ): Promise<void> {
    for (let i = 0; i < docs.length; i += BATCH_CHUNK_SIZE) {
      const batch = writeBatch(this.db);
      for (const item of docs.slice(i, i + BATCH_CHUNK_SIZE)) {
        batch.set(doc(target as CollectionReference, item.id), stripUndefined(item.data) as Record<string, unknown>);
      }
      await batch.commit();
    }
  }

  private rewriteDoc(
    data: FirestoreDoc,
    projectId: string,
    authorId: string,
    overrides: Record<string, unknown> = {},
  ): Record<string, unknown> {
    const rewritten = {
      ...data,
      projectId,
      authorId,
      ...overrides,
      updatedAt: overrides.updatedAt ?? serverTimestamp(),
    };
    return stripUndefined(rewritten) as Record<string, unknown>;
  }
}

export const authorCloneService = new AuthorCloneService();
